import Link from 'next/link';
import { cn } from '@/util/cn';

import { ReactComponent as SvgElevation } from 'public/assets/icons/elevation.svg';
import { ReactComponent as SvgArrow } from 'public/assets/icons/rightArrowInCircle.svg';
import { ReactComponent as SvgArrowGreen } from 'public/assets/icons/greenRightArrowInCircle.svg';
import { ReactComponent as SvgTrails } from 'public/assets/icons/trails.svg';
import { ReactComponent as SvgLifts } from 'public/assets/icons/overview-lifts.svg';
import { ReactComponent as SvgSnowfall } from 'public/assets/icons/snowfall.svg';
import { ReactComponent as SvgArrowWhiteBg } from 'public/assets/icons/arrow-white-bg26.svg';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Routes } from '@/routes';
import { DistanceUnit, SnowUnit } from '@/components/blocks/Unit';
import {
  IResortTrailmapPage, IResortStrengthsOverview, ISnowfallItem,
  IResortPathInfo,
} from '@/api/resort/types';
import { useDistanceFormatter } from '@/i18n/unitFormat';
import { locale } from '@/i18n/constants';
import { Category } from '../ResortStrengths/Category';
import { BestTimeSnow } from '../BestTimeSnowUpdated';
import {
  mainClass,
  resortInfoClass,
  grayContainerClass,
  secondLineContainerClass,
  rowClass,
  smColClass,
  columnClass,
  columnHeaderClass,
  mainTextClass,
  greenMainTextClass,
  secondaryTextClass,
  flex1Class,
  columnInnerClass,
  bestKnownClass,
  bestKnownContainerClass,
  bestKnownHeaderClass,
} from './styles';

interface Props {
  trailmap: IResortTrailmapPage;
  resortStrengths: IResortStrengthsOverview;
  snowfallInfoAnnual: ISnowfallItem[] | null;
  snowfallInfoMonthly: ISnowfallItem[] | null;
  pathInfo: IResortPathInfo;
  annualSnowfall?: number;
  isSummerMode?: boolean;
}

export const ResortGeneralInfo = ({
  trailmap, resortStrengths, annualSnowfall, pathInfo, snowfallInfoMonthly, isSummerMode = false,
}: Props) => {
  const { elevation, runs } = trailmap;
  const { t } = useOTSTranslation('common');

  const totalLifts = Object.values(trailmap.lifts).reduce((prevValue, value) => prevValue + value, 0);
  const liftTypes = Object.values(trailmap.lifts).reduce((prevValue, value) => (value ? prevValue + 1 : prevValue), 0);

  const chartDataForecast = snowfallInfoMonthly ? snowfallInfoMonthly.slice(0, 6) : [];

  const formatAcHa = useDistanceFormatter('acOrHa');

  const skiableTerrainFormatted = formatAcHa(runs.skiableAc);
  const [skiableTerrain] = skiableTerrainFormatted.split(' ');

  return (
    <div className={mainClass}>
      <div className={resortInfoClass}>
        <div className={columnClass}>
          {!!trailmap && !!elevation && (
            <div className={rowClass}>
              <div className={cn(columnClass, grayContainerClass)}>
                <div className={columnHeaderClass}>
                  <div className={rowClass}>
                    <div className="row-start">
                      <span><SvgElevation width={26} height={19} /></span>
                      {t('resort.overview.elevation')}
                    </div>
                    <div className="row-end">
                      {isSummerMode ? (
                        <Link href={Routes.resortTripPlanningTrailMap(pathInfo)}>
                          <SvgArrowGreen width={26} height={26} />
                        </Link>
                      ) : (
                        <Link href={Routes.resortTripPlanningTrailMap(pathInfo)}>
                          <SvgArrow width={26} height={26} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div className={cn(rowClass, smColClass)}>
                    <div className="flex flex-col">
                      <div className={cn(mainTextClass, isSummerMode && greenMainTextClass)}>
                        <DistanceUnit values={elevation.base} type="mOrFeetShort" formatToLocale />
                      </div>
                      <div className={secondaryTextClass}>{t('common.base')}</div>
                    </div>
                    <div className="flex flex-col">
                      <div className={cn(mainTextClass, isSummerMode && greenMainTextClass)}>
                        <DistanceUnit values={elevation.summit} type="mOrFeetShort" formatToLocale />
                      </div>
                      <div className={secondaryTextClass}>{t('common.summit')}</div>
                    </div>
                    <div className="flex flex-col">
                      <div className={cn(mainTextClass, isSummerMode && greenMainTextClass)}>
                        <DistanceUnit values={elevation.verticalDrop} type="mOrFeetShort" formatToLocale />
                      </div>
                      <div className={secondaryTextClass}>{t('resort.overview.verticalDrop')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isSummerMode && (
            <div className={cn(rowClass, smColClass)}>
              {!!trailmap?.runs?.skiableAc && !!trailmap?.runs?.total && (
                <div className={cn(columnClass, grayContainerClass, secondLineContainerClass)}>
                  <div className={columnHeaderClass}>
                    <div className={rowClass}>
                      <div className="row-start">
                        <span><SvgTrails width={16} height={24} /></span>
                        {t('region.skiReport.trails-abb')}
                      </div>
                      <div className="row-end">
                        <Link href="#terrain_overview_widget">
                          <SvgArrow width={26} height={26} />
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className={rowClass}>
                      <div className="flex flex-col">
                        <div className={mainTextClass}>{runs.total}</div>
                        <div className={secondaryTextClass}>
                          {Number(skiableTerrain).toLocaleString([locale, 'en-US'])}
                          {' '}
                          {t('resort.overview.acresHectars')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {!!trailmap?.lifts && (
                <div className={cn(columnClass, grayContainerClass, secondLineContainerClass)}>
                  <div className={columnHeaderClass}>
                    <div className={rowClass}>
                      <div className="row-start">
                        <span><SvgLifts width={26} height={19} /></span>
                        {t('resort.overview.lifts')}
                      </div>
                      <div className="row-end">
                        <Link href="#total_lifts_widget">
                          <SvgArrow width={30} height={20} />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className={rowClass}>
                      <div className="flex flex-col">
                        <div className={mainTextClass}>
                          {totalLifts}
                        </div>
                        <div className={secondaryTextClass}>
                          {liftTypes}
                          {' '}
                          {t('resort.overview.types')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!!annualSnowfall && !!chartDataForecast && !isSummerMode && (
            <div className={rowClass}>
              <div className={cn(columnClass, grayContainerClass)}>
                <div className={columnHeaderClass}>
                  <div className={rowClass}>
                    <div className="row-start">
                      <span><SvgSnowfall width={24} height={20} /></span>
                      {t('region.skiReport.snowfall')}
                    </div>
                    <div className="row-end">
                      <Link href={Routes.resortSnowfall(pathInfo)}>
                        <SvgArrow width={26} height={26} />
                      </Link>
                    </div>
                  </div>
                </div>
                <div>
                  <div className={cn(rowClass, smColClass)}>
                    <div className={flex1Class}>
                      <div className={mainTextClass}><SnowUnit values={annualSnowfall} /></div>
                      <div className={secondaryTextClass}>
                        {t('resort.overview.annualSnowfall')}
                      </div>
                    </div>
                    <div className={cn(columnInnerClass, flex1Class)}>
                      <BestTimeSnow snowfallInfo={chartDataForecast} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!!resortStrengths.ratings.length && !isSummerMode && (
            <section className={bestKnownClass}>
              <div className={bestKnownContainerClass}>
                <div className={rowClass}>
                  <h4 className={bestKnownHeaderClass}>{t('resort.skiReport.bestKnownFor')}</h4>
                  <Link href={Routes.resortTripPlanningReviews(pathInfo)}>
                    <SvgArrowWhiteBg width={26} height={26} />
                  </Link>
                </div>
                <div>
                  <ul>
                    {resortStrengths.ratings.map((resort) => (
                      <li key={resort.region.uuid}>
                        <Category
                          key={`${resort.position}-${resort.category}-${resort.region.uuid}`}
                          resortStrengths={resort}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
